<template>
  <div>
    <div class="header">
      <span class="title">代码贡献 TOP100</span>
    </div>

    <div style="text-align: left; margin-bottom: 16px;">
      <span class="caption-and-select-container">
        <span class="caption">贡献时间</span>
        <a-select v-model:value="year" class="select">
          <a-select-option v-for="opt in yearOptions" :key="opt.key" :value="opt.value">
            {{ opt.label }}
          </a-select-option>
        </a-select>
      </span>
      <span class="caption-and-select-container">
        <span class="caption">项目维度</span>
        <a-select v-model:value="project" class="select">
          <a-select-option v-for="opt in projectOptions" :key="opt.key" :value="opt.value">
            {{ opt.label }}
          </a-select-option>
        </a-select>
      </span>
      <span class="caption-and-select-container">
        <span class="caption">组织维度</span>
        <a-select v-model:value="employerType" class="select">
          <a-select-option v-for="opt in employerOptions" :key="opt.key" :value="opt.value">
            {{ opt.label }}
          </a-select-option>
        </a-select>
      </span>
    </div>

    <a-spin :spinning="spinning">
      <a-table :columns="columns" :data-source="tableData" :pagination="pagination" :rowKey="row => row.rank"
        :rowClassName="rowClassName">
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'rank'">
            <div class="rank-cell">
              <span class="rank-number">{{ record.rank }}</span>
              <component :is="rankIcon(record.rank)" v-if="rankIcon(record.rank)"
                :style="{ color: rankColor(record.rank) }" class="rank-icon" />
            </div>
          </template>
          <template v-else-if="column.dataIndex === 'contributor'">
            <span :class="{ 'top-name': record.rank <= 3 }">{{ record.contributor }}</span>
          </template>
          <template v-else-if="column.dataIndex === 'employer'">
            {{ record.employer || '—' }}
          </template>
          <template v-else-if="column.dataIndex === 'code'">
            <div class="metric-cell">
              <div class="metric-value">{{ formatNumber(record.code) }}</div>
              <div class="bar-bg">
                <div class="bar-fill" :style="{ width: codePercent(record) + '%', backgroundColor: '#00D676' }"></div>
              </div>
            </div>
          </template>
        </template>
      </a-table>
    </a-spin>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { CrownOutlined, TrophyOutlined, StarOutlined } from '@ant-design/icons-vue';
import axios from 'axios';
import { notification } from 'ant-design-vue';

const spinning = ref(false);

const years = Array.from({ length: 2025 - 2020 + 1 }, (_, i) => 2020 + i);
const yearOptions = [
  { key: 'all', label: '全部', value: null },
  ...years.map((y) => ({ key: y, label: String(y), value: y }))
];
const projectOptions = [
  { key: 'all', label: '全部', value: null },
  { key: 'master', label: '主干', value: 'openharmony' },
  { key: 'sig', label: '孵化仓', value: 'openharmony-sig' },
  { key: 'tpc', label: 'TPC', value: 'openharmony-tpc' }
];
const employerOptions = [
  { key: 'all', label: '全部', value: null },
  { key: 'huawei', label: '华为', value: 'HUAWEI' },
  { key: 'non_huawei', label: '非华为', value: 'NON_HUAWEI' }
];

const year = ref(null);
const project = ref(null);
const employerType = ref(null);
const currentPage = ref(1);
const pagination = computed(() => ({
  pageSize: 20,
  current: currentPage.value,
  onChange: (page) => {
    currentPage.value = page;
  }
}));

const columns = [
  { title: '排名', dataIndex: 'rank', key: 'rank', width: 80, align: 'center' },
  { title: '贡献者', dataIndex: 'contributor', key: 'contributor', width: 220 },
  { title: '雇主信息', dataIndex: 'employer', key: 'employer', width: 200 },
  { title: '是否宣传', dataIndex: 'isPromoted', key: 'isPromoted', width: 120, align: 'center' },
  { title: '代码修改量（行）', dataIndex: 'code', key: 'code', width: 220 }
];

const tableData = ref([]);

const promotionEmailSet = new Set([
  'wangshi@kaihong.com',
  'tang_lizhen@runkaihong.com.cn',
  'bayanxing@kaihongdigi.com',
  'wangshi@kaihongdigi.com',
  'goujingjing@kaihong.com'
]);

const maxCode = computed(() => tableData.value[0]?.code || 1);

const codePercent = (row) => Math.max(0, Math.min(100, (row.code / maxCode.value) * 100));

const formatNumber = (n) => {
  if (n === null || n === undefined) return '';
  const num = typeof n === 'number' ? n : Number(n);
  return Number.isNaN(num) ? n : num.toLocaleString();
};

const rowClassName = (_record, index) => (currentPage.value === 1 && index < 3 ? 'top-rank-row' : ''); // 仅第一页前三名高亮

const rankIcon = (rank) => {
  if (rank === 1) return CrownOutlined;
  if (rank === 2) return TrophyOutlined;
  if (rank === 3) return StarOutlined;
  return null;
};

const rankColor = (rank) => {
  if (rank === 1) return '#f59e0b';
  if (rank === 2) return '#9ca3af';
  if (rank === 3) return '#b45309';
  return '#6b7280';
};

const mapRows = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map((item, idx) => {
    const candidateEmail = String(
      item?.email || item?.contributorEmail || item?.mail || item?.contributor || ''
    ).trim().toLowerCase();

    return {
      rank: idx + 1,
      contributor: item.contributor,
      employer: item.employer,
      isPromoted: promotionEmailSet.has(candidateEmail) ? '是' : '否',
      code: Number(item.codeLines) || 0
    };
  });
};

const fetchTable = async () => {
  spinning.value = true;
  try {
    const params = { year: year.value, project: project.value, employerType: employerType.value };
    const res = await axios.get('/metric/contributorRank', { params });
    if (res?.data?.code !== 200 || !Array.isArray(res.data?.data)) {
      throw new Error(res?.data?.msg || '请求失败');
    }
    tableData.value = mapRows(res.data.data);
  } catch (error) {
    tableData.value = [];
    notification.error({ message: '数据加载失败', description: error?.message || error });
  } finally {
    spinning.value = false;
  }
};

watch([year, project, employerType], fetchTable, { immediate: true });
</script>

<style scoped>
.caption-and-select-container {
  text-align: left;
  margin-right: 24px;
}

.caption {
  margin-right: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #333333;
}

.select {
  width: 120px;
}

.header {
  text-align: left;
  margin-bottom: 20px;
}

.title {
  font-size: 20px;
  font-weight: 600;
  color: #111111;
}

.metric-cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.metric-value {
  font-variant-numeric: tabular-nums;
}

.bar-bg {
  width: 100%;
  height: 6px;
  background-color: transparent;
  border-radius: 4px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 4px;
}

.rank-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.rank-number {
  font-weight: 700;
  font-size: 18px;
}

.rank-icon {
  font-size: 18px;
}

.top-name {
  font-weight: 700;
}

:deep(.ant-table-tbody > tr.top-rank-row) {
  /* deep 覆盖 Ant Table 行背景 */
  background: linear-gradient(90deg, #FDFBE8 0%, #FFFFFF 100%);
}

:deep(.ant-table-tbody > tr.top-rank-row > td) {
  background: transparent;
}
</style>